/**
 * Shared name-finder fetch/parse core (#1138), factored out of explorer's
 * `useNameFinder` so property-finder's own hook can reuse the identical
 * fetch/parse/threshold logic without duplicating it. Pure — no React. The
 * probe/query state machines stay in each widget's own hook.
 *
 * Endpoint contract (central#2885): `GET
 * /public-api/v1/{feedId}/feed/property-names` with `q`/`page`/`limit` — the
 * ONLY verified-live params. Do not add others.
 */
import { homerunnerApiBaseUrl } from '../../hr'

export type NameFinderMatch = { name: string; slug: string }

export const PAGE_SIZE = 200
export const MAX_PROBE_PAGES = 25 // 5000 (threshold max) / 200

export const propertyNamesUrl = (feedId: number, params: Record<string, string>) => {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/${feedId}/feed/property-names`)
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v)
  return url.toString()
}

/**
 * Defensive `{name, slug}[]` validator. Returns `null` (not `[]`) on any
 * STRUCTURAL failure so callers can tell "empty results" from "malformed
 * response"; individual rows with a non-string name/slug or an empty slug
 * are skipped, not fatal.
 */
export const parseRows = (data: unknown): NameFinderMatch[] | null => {
  if (typeof data !== 'object' || data === null) return null
  const result = (data as { result?: unknown }).result
  if (!Array.isArray(result)) return null
  const rows: NameFinderMatch[] = []
  for (const row of result) {
    if (typeof row !== 'object' || row === null) return null
    const { name, slug } = row as { name?: unknown; slug?: unknown }
    if (typeof name !== 'string' || typeof slug !== 'string' || !slug) continue
    rows.push({ name, slug })
  }
  return rows
}

export const byName = (a: NameFinderMatch, b: NameFinderMatch) => a.name.localeCompare(b.name)

/**
 * Outcome of a probe-then-cache-or-query mount probe (#853 semantics):
 * - `full` — a short page proved exhaustion at/below the threshold; `rows`
 *   is the complete portfolio, sorted, ready for client-side matching.
 * - `query` — the portfolio overflows the threshold (or exhaustion was never
 *   proven); each lookup must query the server.
 * - `unavailable` — ANY failed/malformed response: render nothing (SHIPS
 *   DARK behind central#2885), never a broken UI.
 */
export type NameProbeOutcome =
  | { kind: 'full'; rows: NameFinderMatch[] }
  | { kind: 'query' }
  | { kind: 'unavailable' }

// Module-level probe cache (#1142 review): multiple finder instances on one
// page share ONE probe per feed instead of each re-paging
// /feed/property-names. Keyed by feedId; the threshold rides along so a
// config-panel threshold change replaces the entry and re-probes
// (once-per-config semantics).
const probeCache = new Map<number, { threshold: number; promise: Promise<NameProbeOutcome> }>()

/**
 * Page `/feed/property-names` until a short page proves exhaustion or the
 * collected count overflows `threshold`. Never rejects — failures resolve to
 * `{ kind: 'unavailable' }` so every sharer gets the same ships-dark answer.
 */
export const probePropertyNames = (feedId: number, threshold: number): Promise<NameProbeOutcome> => {
  const cached = probeCache.get(feedId)
  if (cached && cached.threshold === threshold) return cached.promise
  const promise = (async (): Promise<NameProbeOutcome> => {
    try {
      const collected: NameFinderMatch[] = []
      for (let page = 1; page <= MAX_PROBE_PAGES; page++) {
        const res = await fetch(propertyNamesUrl(feedId, { page: String(page), limit: String(PAGE_SIZE) }))
        if (!res.ok) throw new Error(`property-names ${res.status}`)
        const rows = parseRows(await res.json())
        if (rows === null) throw new Error('property-names malformed payload')
        collected.push(...rows)
        if (collected.length > threshold) return { kind: 'query' }
        if (rows.length < PAGE_SIZE) return { kind: 'full', rows: collected.sort(byName) }
      }
      // Only a short page proves exhaustion — a partial cache would lie.
      return { kind: 'query' }
    } catch {
      return { kind: 'unavailable' }
    }
  })()
  probeCache.set(feedId, { threshold, promise })
  return promise
}

/** Test-only: probe outcomes are cached for the page lifetime. */
export const clearNameProbeCacheForTests = () => probeCache.clear()
